import { toast } from 'vue-sonner'
import { SIGN_STATUS } from '@deepcloud/api-deep-sign'
import { humanFileSize } from '@deepcloud/deep-ui-utils'
import { useApi } from './api'
import { useNodePolicy } from './node/node-policy'
import { useOrganization } from './organization/organization'
import type { Node } from '@deepcloud/api-deep-box'
import type { SignStatus } from '@deepcloud/api-deep-sign'

// decimal value 40MB
const SIGN_MAX_SIZE = 40000000

export function useDeepSign() {
  const { t } = useI18n()
  const router = useRouter()

  const { currentContextOrgId } = useOrganization()

  const { canSign } = useNodePolicy()
  const { deepSignAPI } = useApi()

  /**
   * Check is user can open the document on DeepSign
   * @param item
   * @return {Promise<boolean>}
   */
  async function canOpenDocumentOnDeepsign(item: Node): Promise<boolean> {
    if (!item) return false
    // TODO : this is a workaround waiting for https://jira.abacus.ch/browse/DEEPBOX-1185
    // see https://jira.abacus.ch/browse/DEEPBOX-2074

    if (item.signStatus === null) {
      return true
    }

    try {
      //TODO: WARNING! API is deprecated
      const res = await deepSignAPI.value.documents.get({
        filterDeepboxDocumentId: item.nodeId,
        filterDocumentStatus: item.signStatus,
      })
      const resData = res?.data

      const blockedStatuses: SignStatus[] = [
        SIGN_STATUS.IN_PROGRESS,
        SIGN_STATUS.SIGNED,
      ]

      if (
        canSign(item) &&
        resData.size === 0 &&
        item.signStatus &&
        blockedStatuses.includes(item.signStatus)
      ) {
        return false
      }

      if (canSign(item) && resData.size === 0) {
        return true
      }

      return resData?.size > 0
    } catch (e) {
      console.log(e)
      return false
    }
  }

  function getDeepSignDeepBoxUrl(node: Node, orgId: string) {
    const signStatus = node.signStatus
    let url = import.meta.env.VITE_DEEPSIGN_URL

    switch (signStatus) {
      case 'in-progress':
        url += `/deepbox/${node.nodeId}/pending`
        break
      case 'signed':
        url += `/deepbox/${node.nodeId}/signed`
        break
      default:
        url += `/deepbox/${node.nodeId}`
        break
    }

    const deepSignUrl = new URL(url)

    deepSignUrl.searchParams.append('backUrl', window.location.href)

    deepSignUrl.searchParams.append('companyId', orgId)

    return deepSignUrl.href
  }

  function isNodeTooBigForSign(node: Node) {
    return node.size > SIGN_MAX_SIZE
  }

  function openDeepSignDocument(node: Node) {
    if (isNodeTooBigForSign(node)) {
      toast.error(
        t('messages.error.deepsign_file_max_size', {
          size: humanFileSize(SIGN_MAX_SIZE, 0),
        }),
      )
      return
    }
    const url = getDeepSignDeepBoxUrl(node, currentContextOrgId.value ?? '')

    window.open(url, '_self')
  }

  function getSignatureStatusIcon(node: Node) {
    const status = node?.signStatus || ''
    switch (status) {
      case SIGN_STATUS.IN_PROGRESS:
        return 'fas fa-hourglass-start'
      case SIGN_STATUS.SIGNED:
        return 'far fa-circle-check'
      default:
        return 'fas fa-pen-nib'
    }
  }

  async function openDeepSignOrPreviewDocument(node: Node) {
    if (node?.signStatus) {
      const canOpen = await canOpenDocumentOnDeepsign(node)
      if (canOpen) {
        openDeepSignDocument(node)
      } else {
        // Fallback if user can not open DeepSign
        void router.push({
          name: 'node-preview',
          params: {
            node: node.nodeId,
          },
        })
      }
    } else {
      openDeepSignDocument(node)
    }
  }

  return {
    openDeepSignDocument,
    getSignatureStatusIcon,
    canOpenDocumentOnDeepsign,
    openDeepSignOrPreviewDocument,
  }
}
